import { Conditional } from '@/lib/components/core/conditional/conditional';
import { Typography, Form, Space, Checkbox, Radio } from 'antd';
import generatePicker from 'antd/es/date-picker/generatePicker';
import { Divider } from 'antd/lib';
import dayjs, { Dayjs } from 'dayjs';
import dayjsGenerateConfig from 'rc-picker/lib/generate/dayjs';
import { FC, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface WaterLozingSituatieInputDto {
  betrekkingTotTeeltwijze: number;
  extraStikstofVerwijderd?: boolean;
  waterlozingSituaties: {
    value: number;
    begindatum: string;
    einddatum: string;
  }[];
}

interface Properties {
  teeltwijze: number;
}

export const ZuiveringswijzeV2: FC<Properties> = ({ teeltwijze }) => {
  const { t } = useTranslation('input');
  const { Title } = Typography;
  const form = Form.useFormInstance();

  const currentWaterlozingSituatieIndex = teeltwijze;

  useEffect(() => {
    const fieldPath = ['waterLozingSituatie', teeltwijze];
    if (form.getFieldValue([...fieldPath, 'initialized'])) return;
    const existing: WaterLozingSituatieInputDto[] = form.getFieldValue('waterlozingSituaties') || [];
    const saved = existing.find(item => item.betrekkingTotTeeltwijze === teeltwijze);
    const values: Record<string, unknown> = {
      initialized: true,
      extraStikstofVerwijderd: saved?.extraStikstofVerwijderd,
    };
    for (const optionId of [1, 2, 3, 4, 5, 6]) {
      const situation = saved?.waterlozingSituaties.find(item => item.value === optionId);
      values[`options.${optionId}.checked`] = !!situation;
      values[`options.${optionId}.startDate`] = situation?.begindatum ? dayjs(situation.begindatum) : undefined;
      values[`options.${optionId}.endDate`] = situation?.einddatum ? dayjs(situation.einddatum) : undefined;
    }
    form.setFieldValue(fieldPath, values);
  }, [teeltwijze, form]);

  const registratiejaar = form.getFieldValue('registratiejaar');

  const datePickerConfig = dayjsGenerateConfig;
  const DatePickerComponent = generatePicker<Dayjs>(datePickerConfig);

  const startOfYear = useMemo(() => {
    if (!registratiejaar) return undefined;
    return dayjs.utc(new Date(Date.UTC(registratiejaar, 0, 1)));
  }, [registratiejaar]);
  const endOfYear = useMemo(() => {
    if (!registratiejaar) return undefined;
    return dayjs.utc(new Date(Date.UTC(registratiejaar, 11, 31)));
  }, [registratiejaar]);

  const disableOutOfYearRange = (currentDate: Dayjs) => {
    if (!startOfYear || !endOfYear) return false;
    return currentDate.isBefore(startOfYear) || currentDate.isAfter(endOfYear);
  };

  // Define options configuration once; ids map directly to translation keys and value for DTO
  const options = [1, 2, 3, 4, 5, 6].map(n => ({ id: n, value: n }));

  // Individual field watchers (explicit to ensure updates propagate for Conditional rendering)
  const checked1 = Form.useWatch(['waterLozingSituatie', currentWaterlozingSituatieIndex, 'options.1.checked'], form) || false;
  const checked2 = Form.useWatch(['waterLozingSituatie', currentWaterlozingSituatieIndex, 'options.2.checked'], form) || false;
  const checked3 = Form.useWatch(['waterLozingSituatie', currentWaterlozingSituatieIndex, 'options.3.checked'], form) || false;
  const checked4 = Form.useWatch(['waterLozingSituatie', currentWaterlozingSituatieIndex, 'options.4.checked'], form) || false;
  const checked5 = Form.useWatch(['waterLozingSituatie', currentWaterlozingSituatieIndex, 'options.5.checked'], form) || false;
  const checked6 = Form.useWatch(['waterLozingSituatie', currentWaterlozingSituatieIndex, 'options.6.checked'], form) || false;
  const extraStikstofVerwijderd = Form.useWatch(['waterLozingSituatie', currentWaterlozingSituatieIndex, 'extraStikstofVerwijderd'], form);

  const startDate1 = Form.useWatch(['waterLozingSituatie', currentWaterlozingSituatieIndex, 'options.1.startDate'], form) as Dayjs | undefined;
  const startDate2 = Form.useWatch(['waterLozingSituatie', currentWaterlozingSituatieIndex, 'options.2.startDate'], form) as Dayjs | undefined;
  const startDate3 = Form.useWatch(['waterLozingSituatie', currentWaterlozingSituatieIndex, 'options.3.startDate'], form) as Dayjs | undefined;
  const startDate4 = Form.useWatch(['waterLozingSituatie', currentWaterlozingSituatieIndex, 'options.4.startDate'], form) as Dayjs | undefined;
  const startDate5 = Form.useWatch(['waterLozingSituatie', currentWaterlozingSituatieIndex, 'options.5.startDate'], form) as Dayjs | undefined;
  const startDate6 = Form.useWatch(['waterLozingSituatie', currentWaterlozingSituatieIndex, 'options.6.startDate'], form) as Dayjs | undefined;

  const endDate1 = Form.useWatch(['waterLozingSituatie', currentWaterlozingSituatieIndex, 'options.1.endDate'], form) as Dayjs | undefined;
  const endDate2 = Form.useWatch(['waterLozingSituatie', currentWaterlozingSituatieIndex, 'options.2.endDate'], form) as Dayjs | undefined;
  const endDate3 = Form.useWatch(['waterLozingSituatie', currentWaterlozingSituatieIndex, 'options.3.endDate'], form) as Dayjs | undefined;
  const endDate4 = Form.useWatch(['waterLozingSituatie', currentWaterlozingSituatieIndex, 'options.4.endDate'], form) as Dayjs | undefined;
  const endDate5 = Form.useWatch(['waterLozingSituatie', currentWaterlozingSituatieIndex, 'options.5.endDate'], form) as Dayjs | undefined;
  const endDate6 = Form.useWatch(['waterLozingSituatie', currentWaterlozingSituatieIndex, 'options.6.endDate'], form) as Dayjs | undefined;

  const optionValues: Record<number, { checked: boolean; startDate?: Dayjs; endDate?: Dayjs; value: number }> = {
    1: { checked: checked1, startDate: startDate1, endDate: endDate1, value: 1 },
    2: { checked: checked2, startDate: startDate2, endDate: endDate2, value: 2 },
    3: { checked: checked3, startDate: startDate3, endDate: endDate3, value: 3 },
    4: { checked: checked4, startDate: startDate4, endDate: endDate4, value: 4 },
    5: { checked: checked5, startDate: startDate5, endDate: endDate5, value: 5 },
    6: { checked: checked6, startDate: startDate6, endDate: endDate6, value: 6 },
  };

  useEffect(() => {
    if (teeltwijze === undefined || teeltwijze === null) return;
    const existing: WaterLozingSituatieInputDto[] = form.getFieldValue('waterlozingSituaties') || [];

    const selectedSituaties = options.reduce<WaterLozingSituatieInputDto['waterlozingSituaties'][number][]>((acc, option) => {
      const fieldPath = ['waterLozingSituatie', currentWaterlozingSituatieIndex];
      const data = {
        checked: form.getFieldValue([...fieldPath, `options.${option.id}.checked`]),
        startDate: form.getFieldValue([...fieldPath, `options.${option.id}.startDate`]),
        endDate: form.getFieldValue([...fieldPath, `options.${option.id}.endDate`]),
      };
      if (data.checked && data.startDate && data.endDate) {
        acc.push({
          value: option.value,
          begindatum: data.startDate.toISOString(),
          einddatum: data.endDate.toISOString(),
        });
      }
      return acc;
    }, []);

    const filtered = existing.filter(w => w.betrekkingTotTeeltwijze !== teeltwijze);
    if (selectedSituaties.length > 0) {
      const option2Selected = selectedSituaties.some(situation => situation.value === 2);
      const payload: any = { betrekkingTotTeeltwijze: teeltwijze, waterlozingSituaties: selectedSituaties };
      const extraStikstof = form.getFieldValue(['waterLozingSituatie', currentWaterlozingSituatieIndex, 'extraStikstofVerwijderd']);
      if (option2Selected && typeof extraStikstof === 'boolean') {
        payload.extraStikstofVerwijderd = extraStikstof;
      }
      filtered.push(payload);
    }
    form.setFieldsValue({ waterlozingSituaties: filtered });
  }, [optionValues, teeltwijze, form, options, extraStikstofVerwijderd]);

  const renderOption = (option: { id: number; value: number }) => {
    const optionData = optionValues[option.id];
    const isSelected = optionData.checked;

    return (
      <div key={option.id}>
        <Form.Item
          name={[`waterLozingSituatie`, currentWaterlozingSituatieIndex, `options.${option.id}.checked`]}
          valuePropName="checked"
          initialValue={false}
          style={{ marginTop: -20, marginBottom: 5 }}>
          <Checkbox>{t(`form.waterdischargeV2.option_${option.id}`)}</Checkbox>
        </Form.Item>
        <Conditional condition={isSelected === true}>
          <Space direction="horizontal">
            <Form.Item
              name={[`waterLozingSituatie`, currentWaterlozingSituatieIndex, `options.${option.id}.startDate`]}
              rules={[{ required: isSelected, message: t('form.waterdischargeV2.startDateRequired') }]}>
              <DatePickerComponent
                format={'DD-MM-YYYY'}
                defaultPickerValue={registratiejaar ? dayjs.utc(new Date(Date.UTC(registratiejaar, 0, 1))) : undefined}
                disabledDate={disableOutOfYearRange}
                placeholder={t('form.waterdischargeV2.selectStartDate')}
              />
            </Form.Item>
            <Form.Item
              name={[`waterLozingSituatie`, currentWaterlozingSituatieIndex, `options.${option.id}.endDate`]}
              rules={[{ required: isSelected, message: t('form.waterdischargeV2.endDateRequired') }]}>
              <DatePickerComponent
                format={'DD-MM-YYYY'}
                defaultPickerValue={registratiejaar ? dayjs.utc(new Date(Date.UTC(registratiejaar, 0, 1))) : undefined}
                disabledDate={disableOutOfYearRange}
                placeholder={t('form.waterdischargeV2.selectEndDate')}
              />
            </Form.Item>
          </Space>

          <Conditional condition={option.id === 2}>
            <Typography style={{ marginTop: -10 }}>
              <b>{t('form.waterdischargeV2.extraStikstofText')}</b>
            </Typography>
            <Form.Item
              name={['waterLozingSituatie', currentWaterlozingSituatieIndex, 'extraStikstofVerwijderd']}
              rules={
                optionValues[2].checked ? [{ required: true, message: t('form.waterdischargeV2.extraStikstofVerwijderdRequired', 'Selecteer een optie') }] : []
              }>
              <Radio.Group style={{ marginBottom: -20 }}>
                <Space direction="vertical" style={{ marginBottom: 20 }}>
                  <Radio value={true}>{t('form.waterdischargeV2.extraStikstofTextJa')}</Radio>
                  <Radio value={false}>{t('form.waterdischargeV2.extraStikstofTextNee')}</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
          </Conditional>
          <Divider style={{ marginTop: -10, marginBottom: 10 }}></Divider>
        </Conditional>
      </div>
    );
  };

  return (
    <>
      <Title style={{ margin: '10px 0px' }} level={5}>
        {t('form.waterdischargeV2.title')}
      </Title>
      <Typography>{t('form.waterdischargeV2.topText')}</Typography>

      {/* Hidden field storing DTO array; individual option fields are expected in form state */}
      <Form.Item name="waterlozingSituaties" hidden>
        <input type="hidden" />
      </Form.Item>

      <Form.Item rules={[{ required: true, message: t('form.waterdischargeV2.requiredMessage') }]}>
        <Space size={'middle'} direction="vertical" style={{ width: '100%' }}>
          {options.map(renderOption)}
        </Space>
      </Form.Item>
    </>
  );
};
